import {
  ComponentStyleProps,
  componentStylePropsOf,
} from "../../../lib/styles/props/component-style-props";
import {
  Box,
  ComponentMultiStyleConfig,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  useMultiStyleConfig,
} from "@chakra-ui/react";
import React, {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { CloseIcon } from "@chakra-ui/icons";
import { CMSearchAndSelectDropdownMenu } from "../index";
import { SearchDropdownDisplayValue, SearchDropdownOption } from "../../../lib";
import { hasValue, isNullish } from "../../../lib/util/common-util";

export type CMSearchAndSelectTextboxProps<T extends string | number> = {
  /**
   * ドロップダウンに表示する選択肢一覧。
   */
  options: SearchDropdownOption<T>[];

  onSelectOption: (value: T) => void;

  /**
   * 何か選択されていたら値が入る。
   * そのとき、テキストボックスの値は固定され、入力ができなくなる。
   */
  selected: T | null;

  /**
   * 入力可能な最大文字数。
   */
  maxLength?: number;

  placeholder?: string;

  /**
   * メニューの最大表示数
   */
  menuShowMaxCount?: number;

  /**
   * テキストボックスに自由入力されている文字列。
   */
  inputText?: string | null;
  onChangeInputText?: (value: string) => void;
  onClearInputText?: () => void;

  /**
   * 値を表示値に変換する関数。
   * heading: 各行の手前に、薄い字で表示する部分。必須ではない。
   * main: 表示値。必須。
   */
  toDisplayValue: (value: T) => Promise<SearchDropdownDisplayValue>;

  popoverRightIcon?: ReactElement;

  /**
   * 検索結果が存在しない場合、メニューに表示する文字列
   */
  searchResultNone: string;

  /**
   * メニューの表示制御に使用する。
   */
  searchResultMenuSettings: {
    isOpenMenu: boolean;
    onCloseMenu: () => void;
  };
} & ComponentStyleProps;

/**
 * Inputエリアに入力された文字列で
 * 検索処理を呼び出し、検索結果をメニュー表示する。
 */
export const CMSearchAndSelectTextbox = <T extends string | number>({
  options,
  onSelectOption,

  selected,

  placeholder,
  maxLength = 100,

  menuShowMaxCount = 5,

  inputText,
  onChangeInputText,

  onClearInputText,

  toDisplayValue,

  popoverRightIcon = <CloseIcon />,
  searchResultNone,

  searchResultMenuSettings,

  sx,
  ...rest
}: CMSearchAndSelectTextboxProps<T>) => {
  const styles = useMultiStyleConfig("CMSearchAndSelectTextboxStyles", {});
  const containerStyles = useMemo(
    () => ({
      ...styles.container,
      ...sx,
    }),
    [styles.container, sx],
  );

  const frozen = useMemo(() => hasValue(selected), [selected]);

  const [selectedDisplayValue, setSelectedDisplayValue] = useState<
    string | null
  >(null);
  useEffect(
    () => {
      (async () => {
        if (isNullish(selected)) {
          setSelectedDisplayValue(null);
        } else {
          setSelectedDisplayValue((await toDisplayValue(selected)).mainLabel);
        }
      })();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selected],
  );

  const handleChangeInputText = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (frozen) return;
      onChangeInputText?.(e.target.value);
    },
    [frozen, onChangeInputText],
  );

  return (
    <Box sx={containerStyles} {...rest}>
      <InputGroup sx={styles.inputGroup}>
        <Input
          value={(frozen ? selectedDisplayValue : inputText) ?? ""}
          maxLength={maxLength}
          disabled={frozen}
          placeholder={placeholder}
          onChange={handleChangeInputText}
          sx={styles.inputBase}
        />
        <InputRightElement width="0.5rem">
          <IconButton
            aria-label="close-button"
            isRound={true}
            variant={"ghost"}
            size="xs"
            icon={popoverRightIcon}
            onClick={onClearInputText}
            sx={styles.icon}
          />
        </InputRightElement>
      </InputGroup>
      <CMSearchAndSelectDropdownMenu
        options={options}
        onSelectOption={onSelectOption}
        menuShowMaxCount={menuShowMaxCount}
        searchResultNone={searchResultNone}
        isMenuOpen={searchResultMenuSettings.isOpenMenu}
        onMenuClose={searchResultMenuSettings.onCloseMenu}
        sx={styles.searchMenu}
      />
    </Box>
  );
};

export const CMSearchAndSelectTextboxStyles: ComponentMultiStyleConfig = {
  parts: [
    "container",
    "inputGroup",
    "inputBase",
    "label",
    "icon",
    "searchMenu",
  ],
  baseStyle: {
    container: {},
    inputGroup: {
      w: "300px",
    },
    inputBase: {
      borderColor: "gray.200",
      backgroundColor: "white",
      color: "gray.900",
      _disabled: {
        backgroundColor: "gray.200",
        color: "gray.900",
        cursor: "default",
        textOverflow: "ellipsis",
      },
    },
    label: {
      fontSize: "md",
      color: "gray.700",
    },
    icon: {
      marginLeft: "-25px",
      color: "gray.700",
    },
    searchMenu: {
      position: "relative",
      top: "5px",
    },
  },
};
